// Libraries
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Classes
import { isRouteAllowed } from 'src/app/classes/shared/route-tabs';

@Injectable({ providedIn: 'root' })

export class TabGuard implements CanActivate {
    // Services
    private readonly auth: AuthService = inject(AuthService);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly router: Router = inject(Router);

    constructor() { }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const tab = route.data['tab'] as string;
        const tabType = (route.data['tabType'] as 'dataviz' | 'setting') ?? 'dataviz';
        const user = this.auth.currentUser();

        if (user?.role?.name === 'dev' || user?.role?.name === 'admin') {
            return of(true);
        }

        return this.ensureRequestedProjectLoaded(route).pipe(
            map(() => {
                const project = this.projectService.currentProjectValue;
                const allowedTabs = tabType === 'setting' ? project.settingTabs : project.datavizTabs;

                if (!isRouteAllowed(tab, allowedTabs ?? [])) {
                    this.router.navigate(['/forbidden']);
                    return false;
                }

                return true;
            })
        );
    }

    private ensureRequestedProjectLoaded(route: ActivatedRouteSnapshot): Observable<unknown> {
        const requestedProjectId = route.queryParamMap.get('project');

        if (!requestedProjectId || requestedProjectId === this.projectService.currentProjectValue?.id) {
            return of(null);
        }

        return this.projectService.getProjectById(requestedProjectId).pipe(
            map((project) => this.projectService.setCurrentProject(project)),
            catchError(() => of(null))
        );
    }
}
